import { Alert, Nav, Tab } from "react-bootstrap";
import { IFullLicenceDetails } from "../../../../api/LicenceDetails";
import { ILicenceProfile } from "../../../../api/ILicenceProfile";
import { DocumentsGrid } from "../DocumentsGrid";
import EpaCard from "../../../../leap-components/EpaCard";
import { LicensingActionsGrid } from "../../../../leap-components/LicensingActions/LicensingActionsGrid";
import { ILicensingActionGridFilterValueList } from "../../../../api/ILicensingAction";
import { SubmissionsGrid } from "../../../../leap-components/Submissions/SubmissionsGrid";
import { ISubmissionsGridFilterValueList } from "../../../../api/ISubmission";
import { IChangeRequestsGridFilterValueList } from "../../../../api/IChangeRequests";
import { ChangeRequestsGrid } from "../../../../leap-components/ChangeRequests/ChangeRequestsGrid";
import { ILicenceTransfersGridFilterValueList } from "../../../../api/ILicenceTransfer";
import { LicenceTransfersGrid } from "../../../../leap-components/LicenceTransfers/LicenceTransfersGrid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faInfo } from "@fortawesome/free-solid-svg-icons";
import { InformationIcon } from "../../../../leap-components/icons/LeapIcons";
import { GO_LIVE_DATE } from "../../../../external-apps/Config";

interface SubTabsProps {
  className?: string;
  activeKey: string;
  licence: IFullLicenceDetails;
  licenceProfile: ILicenceProfile;
  licensingActionTypes: ILicensingActionGridFilterValueList;
  licensingActionStatuses: ILicensingActionGridFilterValueList;
  submissionTypes: ISubmissionsGridFilterValueList;
  submissionObjectorTypes: ISubmissionsGridFilterValueList;
  changeRequestsStatuses: IChangeRequestsGridFilterValueList;
  licenceTransfersStatuses: ILicenceTransfersGridFilterValueList;
}

const SubTabs = ({
  className,
  activeKey,
  licence,
  licenceProfile,
  licensingActionTypes,
  licensingActionStatuses,
  submissionTypes,
  submissionObjectorTypes,
  changeRequestsStatuses,
  licenceTransfersStatuses,
}: SubTabsProps) => (
  <div className={"tab " + className}>
    <Tab.Container id="left-tabs-example" defaultActiveKey={activeKey}>
      <Nav variant="tabs">
        <Nav.Item>
          <Nav.Link eventKey="actions">Licensing Actions</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="submissions">Submissions / Objections</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="amendments">Authorisation Amendments</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="transfers">Transfer Requests</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="documents">Complete Document List</Nav.Link>
        </Nav.Item>
      </Nav>
      <Tab.Content>
        <Tab.Pane eventKey="actions">
          <LicensingActionsGrid
            licenceId={licence.licenceid}
            licenceProfile={licenceProfile}
            licensingActionStatuses={licensingActionStatuses}
            licensingActionTypes={licensingActionTypes}
          />
        </Tab.Pane>
        <Tab.Pane eventKey="submissions">
          <SubmissionsGrid
            licenceId={licence.licenceid}
            licenceProfile={licenceProfile}
            submissionTypes={submissionTypes}
            submissionObjectorTypes={submissionObjectorTypes}
          />
        </Tab.Pane>
        <Tab.Pane eventKey="amendments">
          <ChangeRequestsGrid
            licenceId={licence.licenceid}
            licenceProfile={licenceProfile}
            changeRequestsStatuses={changeRequestsStatuses}
          />
        </Tab.Pane>
        <Tab.Pane eventKey="transfers">
          <LicenceTransfersGrid
            licenceId={licence.licenceid}
            licenceProfile={licenceProfile}
            licenceTransfersStatuses={licenceTransfersStatuses}
          />
        </Tab.Pane>
        <Tab.Pane eventKey="documents">
          <DocumentsGrid
            licenceId={licence.licenceid}
            createdByFilter={licence.documentCreatedByFilterValues}
            classificationFilter={licence.documentClassificationFilterValues}
            subclassificationFilter={
              licence.documentSubclassificationFilterValues
            }
          />
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  </div>
);

interface FurtherInfoProps {
  licence: IFullLicenceDetails;
  licenceProfile: ILicenceProfile;
  licensingActionTypes: ILicensingActionGridFilterValueList;
  licensingActionStatuses: ILicensingActionGridFilterValueList;
  submissionTypes: ISubmissionsGridFilterValueList;
  submissionObjectorTypes: ISubmissionsGridFilterValueList;
  changeRequestsStatuses: IChangeRequestsGridFilterValueList;
  licenceTransfersStatuses: ILicenceTransfersGridFilterValueList;
}

const alertColor = "secondary";

const FurtherInformationAlert = () => (
  <Alert variant={alertColor}>
    <div className="alert-icon">
      <InformationIcon />
    </div>
    <div className="alert-message">
      <p>
        <b>Please note:</b> A complete set of records under the sections{" "}
        <i>
          Licensing Actions, Submissions/Objections, Authorisation Amendments,
          and Transfer Requests
        </i>{" "}
        is only available for licences issued or applications on hand{" "}
        <b>on or after {GO_LIVE_DATE}.</b>
      </p>
      <p>
        For documentation related to earlier licences or applications, please
        refer to the <b>Complete Document List</b> tab for all available
        documentation.
      </p>
    </div>
  </Alert>
);

const FurtherInformation = ({
  licence,
  licenceProfile,
  licensingActionTypes,
  licensingActionStatuses,
  submissionTypes,
  submissionObjectorTypes,
  changeRequestsStatuses,
  licenceTransfersStatuses,
}: FurtherInfoProps) => {
  var bodyElements = (
    <div>
      <FurtherInformationAlert />
      <SubTabs
        activeKey="actions"
        licence={licence}
        licenceProfile={licenceProfile}
        licensingActionStatuses={licensingActionStatuses}
        licensingActionTypes={licensingActionTypes}
        submissionTypes={submissionTypes}
        submissionObjectorTypes={submissionObjectorTypes}
        changeRequestsStatuses={changeRequestsStatuses}
        licenceTransfersStatuses={licenceTransfersStatuses}
      />
    </div>
  );
  return <EpaCard title={"Further Information"} content={bodyElements} />;
};

export default FurtherInformation;
